import {
  Badge,
  Caption1,
  Card,
  CardHeader,
  InteractionTag,
  InteractionTagPrimary,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Tag,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { Warning12Regular } from "@fluentui/react-icons";
import type { ReactNode } from "react";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import type { OrganizationTeam, UserResource } from "@/engine/types";
import { fmtCurrency } from "@/lib/format";
import type { ResourceWorkloadSummary } from "@/store/catalog-store";
import { RowMoreMenu } from "./RowMoreMenu";
import { EMPLOYMENT_COLOR, EMPLOYMENT_LABEL, GRADE_LABEL } from "./resource-meta";

const SKILL_LIMIT = 4;

const useStyles = makeStyles({
  card: {
    width: "100%",
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: "pointer",
    ":focus-visible": {
      boxShadow: `0 0 0 2px ${tokens.colorStrokeFocus2}`,
    },
  },
  inactive: {
    opacity: 0.5,
  },
  title: {
    display: "block",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  role: {
    display: "block",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
  },
  actions: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
  },
  meta: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
  },
  alloc: {
    marginLeft: "auto",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    color: tokens.colorNeutralForeground1,
    "& svg": { display: "block" },
  },
  danger: {
    color: tokens.colorPaletteRedForeground1,
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
  plans: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalXXS,
    minWidth: 0,
  },
  skills: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: tokens.spacingHorizontalXXS,
  },
  moreSkills: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  rate: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
  },
  rateValue: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    color: tokens.colorNeutralForeground1,
  },
  linkBtn: {
    border: "none",
    backgroundColor: "transparent",
    padding: 0,
    margin: 0,
    cursor: "pointer",
    color: tokens.colorNeutralForeground2,
    font: "inherit",
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    borderRadius: tokens.borderRadiusMedium,
    ":hover": { color: tokens.colorNeutralForeground1 },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  planList: {
    display: "flex",
    flexDirection: "column",
    minWidth: "220px",
    maxWidth: "320px",
    padding: tokens.spacingVerticalXS,
  },
  planItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    width: "100%",
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    color: tokens.colorNeutralForeground1,
    font: "inherit",
    fontWeight: tokens.fontWeightRegular,
    textAlign: "left",
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
  },
  clip: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  planPct: {
    flexShrink: 0,
    fontVariantNumeric: "tabular-nums",
    color: tokens.colorNeutralForeground3,
  },
});

export function ResourcePersonCard({
  resource,
  team,
  workload,
  masked,
  layout,
  showTeam,
  showEmployment,
  showGrade,
  onOpen,
  onEdit,
  onDelete,
  onOpenProject,
  dragHandle,
}: {
  resource: UserResource;
  team?: OrganizationTeam;
  workload: ResourceWorkloadSummary;
  masked: boolean;
  layout: "card" | "board";
  showTeam: boolean;
  showEmployment: boolean;
  showGrade: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onOpenProject: (projectId: string) => void;
  dragHandle?: ReactNode;
}) {
  const styles = useStyles();
  const pct = Math.round((workload.totalAllocationRatio ?? 0) * 100);
  const employment = resource.employmentType;
  const shownSkills = resource.skills.slice(0, SKILL_LIMIT);
  const hiddenSkills = resource.skills.length - shownSkills.length;

  return (
    <Card
      size="small"
      appearance="outline"
      focusMode="off"
      tabIndex={0}
      aria-label={`${resource.displayName} 프로필 열기`}
      className={mergeClasses(styles.card, !resource.isActive && styles.inactive)}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target === e.currentTarget) onOpen();
      }}
    >
      <CardHeader
        image={<PersonAvatar name={resource.displayName} size={28} />}
        header={
          <Tooltip content={resource.displayName} relationship="description">
            <span className={styles.title}>{resource.displayName}</span>
          </Tooltip>
        }
        description={
          <Tooltip content={resource.roleTitle} relationship="description">
            <span className={styles.role}>{resource.roleTitle}</span>
          </Tooltip>
        }
        action={
          <div className={styles.actions} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            {dragHandle}
            <RowMoreMenu
              label={`${resource.displayName} 메뉴`}
              items={[
                { label: "프로필 보기", onClick: onOpen },
                { label: "수정", onClick: onEdit },
                { label: "삭제", onClick: onDelete },
              ]}
            />
          </div>
        }
      />

      <div className={styles.meta}>
        {showTeam && team && (
          <Badge appearance="outline" size="small">
            {team.name}
          </Badge>
        )}
        {showEmployment && (
          <Badge appearance="tint" size="small" color={EMPLOYMENT_COLOR[employment]}>
            {EMPLOYMENT_LABEL[employment]}
          </Badge>
        )}
        {showGrade && <Caption1 className={styles.muted}>{GRADE_LABEL[resource.grade]}</Caption1>}
        <span className={mergeClasses(styles.alloc, workload.isOverAllocated && styles.danger)}>
          {pct}%
          {workload.isOverAllocated && <Warning12Regular />}
        </span>
      </div>

      {layout === "board" ? (
        <Caption1 className={styles.muted}>{workload.assignments.length > 0 ? `${workload.assignments.length}개 플랜` : "투입 대기"}</Caption1>
      ) : (
        <PlanTags plans={workload.assignments} onOpenProject={onOpenProject} />
      )}

      {layout === "card" && shownSkills.length > 0 && (
        <div className={styles.skills}>
          {shownSkills.map((skill) => (
            <Tag key={skill} appearance="outline" size="extra-small">
              {skill}
            </Tag>
          ))}
          {hiddenSkills > 0 && <span className={styles.moreSkills}>+{hiddenSkills}</span>}
        </div>
      )}

      {layout === "card" && (
        <div className={styles.rate}>
          <Caption1 className={styles.muted}>월 단가</Caption1>
          <span className={styles.rateValue}>{fmtCurrency(resource.defaultMonthlyRate, masked)}</span>
        </div>
      )}
    </Card>
  );
}

function PlanTags({
  plans,
  onOpenProject,
}: {
  plans: ResourceWorkloadSummary["assignments"];
  onOpenProject: (projectId: string) => void;
}) {
  const styles = useStyles();
  if (plans.length === 0) return <Caption1 className={styles.muted}>투입 대기</Caption1>;
  if (plans.length === 1) {
    const plan = plans[0]!;
    return (
      <div className={styles.plans} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <InteractionTag size="extra-small" appearance="outline">
          <InteractionTagPrimary
            onClick={(e) => {
              e.stopPropagation();
              onOpenProject(plan.projectId);
            }}
          >
            <Tooltip content={`${plan.projectName} (${Math.round(plan.allocationRatio * 100)}%)`} relationship="description">
              <span>{plan.projectName}</span>
            </Tooltip>
          </InteractionTagPrimary>
        </InteractionTag>
      </div>
    );
  }
  return (
    <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <Popover>
        <PopoverTrigger disableButtonEnhancement>
          <button type="button" className={styles.linkBtn}>
            {plans.length}개 플랜
          </button>
        </PopoverTrigger>
        <PopoverSurface>
          <div className={styles.planList} role="list">
            {plans.map((plan) => (
              <button
                key={`${plan.projectId}-${plan.projectRole}`}
                type="button"
                role="listitem"
                className={styles.planItem}
                onClick={() => onOpenProject(plan.projectId)}
              >
                <span className={styles.clip}>{plan.projectName}</span>
                <span className={styles.planPct}>{Math.round(plan.allocationRatio * 100)}%</span>
              </button>
            ))}
          </div>
        </PopoverSurface>
      </Popover>
    </div>
  );
}
